import React, { useState, useEffect } from 'react';
import { createBooking } from '../../http/bookingAPI';
import { sendBookingMail } from '../../http/mailAPI';
import '../../styles/bookingForm.scss';
import { fetchCategories } from '../../http/categoryAPI';
import { fetchDoctors } from '../../http/doctorAPI';
import BookingDatePicker from './BookingDatePicker';
import BookingCreatedSuccessModal from './BookingCreatedSuccessModal';
import { i18n } from "@lingui/core";

const BookingForm = ({ onClose }) => {
  const [categories, setCategories] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);

  const [formData, setFormData] = useState({
    patientName: '',
    patientSurname: '',
    phone1: '',
    visitDate: '',
    age: null,
    categoryId: null,
    doctorId: null
  });

  const [errors, setErrors] = useState({});

  const validatePhone = (value) => {
    const regex = /^\+?[0-9\s-]{8,15}$/;
    return regex.test(value);
  };

  useEffect(() => {
    const loadCategories = async () => {
      const data = await fetchCategories();
      setCategories(data);
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const loadDoctors = async () => {
      const data = await fetchDoctors();
      setDoctors(data);
    };
    loadDoctors();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue =
      value === ""
        ? null
        : (name === "categoryId" || name === "doctorId" || name === "age"
          ? Number(value)
          : value);

    setFormData((prev) => {
      let updated = { ...prev, [name]: newValue };

      if (name === "categoryId") {
        updated.doctorId = null;
      }

      if (name === "doctorId") {
        const selectedDoctor = doctors.find(doc => doc.id === Number(value));
        if (selectedDoctor) {
          updated.categoryId = selectedDoctor.categoryId;
        }
      }

      return updated;
    });

    if (name === "phone1") {
      setErrors((prev) => ({
        ...prev,
        phone1: validatePhone(value) ? "" : "Invalid phone number format",
      }));
    }
  };

  // 📧 EMAIL BUILDER
  const buildMailPayload = (data) => {
    const doctor = doctors.find(d => d.id === data.doctorId);
    const category = categories.find(c => c.id === data.categoryId);

    return {
      patientName: data.patientName,
      patientSurname: data.patientSurname,
      phone1: data.phone1,
      age: data.age,
      visitDate: data.visitDate,
      categoryId: data.categoryId,
      doctorId: data.doctorId,
      category: category ? category.nameEN : "",
      doctor: doctor ? `${doctor.firstNameEN} ${doctor.lastNameEN}` : ""
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePhone(formData.phone1)) {
      alert("Please fix phone number");
      return;
    }

    try {
      // 1. Create booking
      await createBooking(formData);

      // 2. Send booking email (NEW CLEAN WAY)
      await sendBookingMail(buildMailPayload(formData));

      // 3. UI success
      setModalVisible(true);

      setFormData({
        patientName: '',
        patientSurname: '',
        phone1: '',
        visitDate: '',
        age: null,
        categoryId: null,
        doctorId: null
      });

      if (onClose) onClose();

    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Something went wrong. Please try again.');
    }
  };

  const filteredDoctors = formData.categoryId
    ? doctors.filter(doc => doc.categoryId === formData.categoryId)
    : doctors;

  return (
    <div className="container mt-5">
      <form onSubmit={handleSubmit} className="booking-form">

        {/* Name + Surname */}
        <div className="form-row booking-first-row">
          <input
            type="text"
            name="patientName"
            value={formData.patientName}
            onChange={handleChange}
            required
            className="form-control"
            placeholder={i18n._("patientNamePlaceholder")}
          />

          <input
            type="text"
            name="patientSurname"
            value={formData.patientSurname}
            onChange={handleChange}
            required
            className="form-control"
            placeholder={i18n._("patientSurnamePlaceholder")}
          />
        </div>

        {/* Phone + Date + Age */}
        <div className="form-row booking-second-row">
          <input
            type="tel"
            name="phone1"
            value={formData.phone1}
            onChange={handleChange}
            required
            className="form-control"
            placeholder={i18n._("phone1Placeholder")}
          />

          <BookingDatePicker formData={formData} setFormData={setFormData} />

          <input
            type="number"
            name="age"
            value={formData.age || ""}
            onChange={handleChange}
            className="form-control"
            min={18}
            placeholder={i18n._("agePlaceholder")}
          />
        </div>

        {/* Category + Doctor */}
        <div className="form-row booking-third-row">
          <select
            name="categoryId"
            value={formData.categoryId || ""}
            onChange={handleChange}
            className="form-control"
          >
            <option value="">{i18n._("categoryPlaceholder")}</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.nameEN}
              </option>
            ))}
          </select>

          <select
            name="doctorId"
            value={formData.doctorId || ""}
            onChange={handleChange}
            className="form-control"
          >
            <option value="">{i18n._("doctorPlaceholder")}</option>
            {filteredDoctors.map(doctor => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.firstNameEN} {doctor.lastNameEN}
              </option>
            ))}
          </select>
        </div>

        {/* Submit */}
        <div className="form-row center">
          <button type="submit" className="booking-button">
            <span>{i18n._("bookButton")}</span>
          </button>
        </div>

      </form>

      <BookingCreatedSuccessModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
      />
    </div>
  );
};

export default BookingForm;